/**
 * Which plots in a pane are a line and its own average, from the catalog's
 * `gap` (owner, 2026-10-06: JAS OBV with EMA, RS to SPY). The values readout
 * then also reads the gap between them, and the pane's corner shows the last
 * bar's.
 *
 * Paired by family **and parameters**: the alias is `<output>_<params>`, so a
 * line and its average from one instance share everything after the head —
 * two instances of a family in one pane stay two pairs.
 */

import type { Pane } from "@qufolio/model";
import type { LegendGap } from "@qufolio/chart-engine";
import type { IndicatorEntry } from "../api/indicators";
import { splitSmoothed } from "./move-to-pane";

const head = (alias: string) => alias.split("_")[0]!.toLowerCase();
const rest = (alias: string) => alias.slice(alias.indexOf("_") + 1 || alias.length);

export function gapsForPane(pane: Pane, catalog: readonly IndicatorEntry[]): LegendGap[] {
  const out: LegendGap[] = [];
  const plots = pane.plots ?? [];
  // **Every smoother against its source**, with no catalog entry: an average of
  // a line is that line's average by construction (owner, 2026-10-06).
  for (const average of plots) {
    const found = splitSmoothed(average.outputAlias);
    const line = found && plots.find((p) => p.outputAlias === found.source);
    if (line) out.push({ line: line.id, average: average.id });
  }
  for (const entry of catalog) {
    if (!entry.gap) continue;
    const lineHead = entry.gap.line.toLowerCase();
    const averageHead = entry.gap.average.toLowerCase();
    for (const line of plots) {
      if (line.indicatorRef !== entry.name || !line.outputAlias || head(line.outputAlias) !== lineHead) continue;
      const average = plots.find(
        (p) =>
          p.indicatorRef === entry.name &&
          p.outputAlias !== undefined &&
          head(p.outputAlias) === averageHead &&
          rest(p.outputAlias) === rest(line.outputAlias!),
      );
      if (average) out.push({ line: line.id, average: average.id });
    }
  }
  return out;
}
